import type {RouteLocationNormalizedLoaded, RouteLocationRaw, RouteRecordRaw} from 'vue-router';
import {useCsvImport} from '@myparcel-frontend/csv-import';
import {SystemMessageType} from '@myparcel-frontend/types';
import {ordersOverviewGuard} from './guards/ordersOverview-guard';
import {buildOrdersOverviewProps} from './props/buildOrdersOverviewProps';
import useFeatures from '@/composables/useFeatures';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';
import {useServiceAccount} from '@/composables/useServiceAccount/useServiceAccount';

const LEGACY_ORDERS_COOKIE = 'orders-legacy=true; path=/; max-age=60; SameSite=Strict';
const CLEAR_LEGACY_ORDERS_COOKIE = 'orders-legacy=; path=/; max-age=0; SameSite=Strict';

export const OrdersView = {
  V2: 'v2',
  V1Vue: 'v1Vue',
  V1Meteor: 'v1Meteor',
} as const;

/**
 * Which orders experience this account gets. ORDER_MANAGEMENT always means v2; legacy accounts get
 * the vue port of v1 while `page.orders.v1vue` is on, and the meteor app otherwise.
 */
export const resolveOrdersView = (): (typeof OrdersView)[keyof typeof OrdersView] => {
  const {hasOrderManagement} = useOrderVersion();

  if (hasOrderManagement) {
    return OrdersView.V2;
  }

  const {isFeatureActive} = useFeatures();
  return isFeatureActive('page.orders.v1vue') ? OrdersView.V1Vue : OrdersView.V1Meteor;
};

export const resolveOrderRoute = (
  onVueRoute: () => RouteLocationRaw | boolean,
  legacyRedirectPath = '/orders',
): RouteLocationRaw | boolean => {
  const view = resolveOrdersView();

  if (view === OrdersView.V1Meteor) {
    document.cookie = LEGACY_ORDERS_COOKIE;

    // legacyRedirectPath may carry its own query; current-location params win (so ?uuid= carries over).
    const target = new URL(legacyRedirectPath, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      target.searchParams.set(key, value);
    });

    window.location.replace(target.toString());
    return false;
  }

  document.cookie = CLEAR_LEGACY_ORDERS_COOKIE;
  return onVueRoute();
};

export const orderRoutes: RouteRecordRaw[] = [
  {
    name: 'orders',
    path: '/orders',
    children: [
      {
        name: 'orders-overview',
        path: '',
        component: () => {
          if (resolveOrdersView() === OrdersView.V1Vue) {
            return import('@/views/Orders/V1/Overview/OrdersOverview.vue');
          }

          const {isFeatureActive} = useFeatures();
          return isFeatureActive('page.orders.mobile')
            ? import('@/views/Orders/OverviewMobile/OverviewMobile.vue')
            : import('@/views/Orders/Overview/Overview.vue');
        },
        meta: {
          title: (t) => t('orders.overview'),
        },
        beforeEnter: (to) => resolveOrderRoute(() => ordersOverviewGuard(to)),
        props: (route: RouteLocationNormalizedLoaded) =>
          resolveOrdersView() === OrdersView.V1Vue ? {} : buildOrdersOverviewProps(route),
      },
      {
        name: 'orders-create-shipment',
        path: ':id/create-shipment',
        props: true,
        component: () => import('@/views/Orders/CreateShipment/CreateShipment.vue'),
        meta: {
          title: (t) => t('orders.newShipment'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
        beforeEnter: () => resolveOrderRoute(() => true),
      },
      {
        name: 'orders-v1-import',
        path: 'v1-import',
        component: () => import('@/views/Orders/V1/Import/OrdersImport.vue'),
        redirect: {name: 'orders-v1-import-overview'},
        // The v1 import page (external providers + csv upload tabs). V2 accounts import via
        // 'orders-import-overview' or 'orders-csv-import'; meteor v1 users are sent to the legacy app.
        beforeEnter: () =>
          resolveOrderRoute(
            () => (resolveOrdersView() === OrdersView.V1Vue ? true : {name: 'orders-overview'}),
            '/orders/import',
          ),
        meta: {
          title: (t) => t('ordersv1.import.title'),
        },
        children: [
          {
            name: 'orders-v1-import-overview',
            path: '',
            components: {
              ordersV1Import: () => import('@/views/Orders/V1/Import/ImportOverview/ImportOverview.vue'),
            },
            meta: {
              title: (t) => t('ordersv1.import.title'),
            },
          },
          {
            name: 'orders-v1-import-edit',
            path: 'edit',
            components: {
              ordersV1Import: () => import('@/views/Orders/V1/Import/ImportEdit/ImportEdit.vue'),
            },
            meta: {
              title: (t) => t('ordersv1.import.title'),
            },
            beforeEnter: () => {
              const {csvFile} = useCsvImport();
              if (csvFile.value) {
                return true;
              }

              return resolveOrderRoute(() => {
                const {isSalesChannelsAccessible} = useServiceAccount();
                return isSalesChannelsAccessible.value ? true : {name: 'orders-overview'};
              }, '/orders/import');
            },
          },
        ],
      },
      {
        name: 'orders-edit',
        path: ':orderId/edit',
        props: true,
        component: () => import('@/views/Orders/V1/Edit/Edit.vue'),
        meta: {
          title: (t) => t('ordersv1.edit.title'),
        },
      },
      {
        name: 'order-package-edit',
        path: ':orderId/package/:packageId',
        props: true,
        component: () => import('@/views/Orders/Packages/Edit/Edit.vue'),
        beforeEnter: [() => resolveOrderRoute(() => true)],
        meta: {
          permissions: ['updateShipment'],
          title: (t) => t('shipment.edit'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
      },
      {
        name: 'orders-csv-import',
        path: 'csv-import',
        component: () => import('@/views/Orders/OrdersCsvImport/OrdersCsvImport.vue'),
        // Csv import is an orders v2 page; v1 users import via 'orders-v1-import'.
        beforeEnter: () =>
          resolveOrderRoute(() => (resolveOrdersView() === OrdersView.V2 ? true : {name: 'orders-overview'})),
        meta: {
          title: (t) => t('ui.fileUpload.importCsv'),
        },
      },
      {
        // The "Te importeren" tab of orders v2: orders discovered on a sales channel.
        name: 'orders-import-overview',
        path: 'import',
        component: () => import('@/views/Orders/OrdersImport/OrdersImport.vue'),
        beforeEnter: () =>
          resolveOrderRoute(() => {
            if (resolveOrdersView() !== OrdersView.V2) {
              return {name: 'orders-overview'};
            }

            const {isSalesChannelsAccessible} = useServiceAccount();
            return isSalesChannelsAccessible.value ? true : {name: 'orders-overview'};
          }, '/orders/import'),
        meta: {
          title: (t) => t('orders.order', 2),
        },
        props: (route: RouteLocationNormalizedLoaded) => ({
          query: {
            query: route.query.query,
            filter: {status: ['DISCOVERED']},
          },
          allShops: false,
        }),
      },
    ],
  },
];
